<script setup lang="ts">
import {computed} from "vue";
import {ClipboardList, Maximize, MessageSquareText, Pencil} from "@lucide/vue";
import {Input} from "@/core/components/ui/input";
import {CARD_SHEET, STATUS_CHIPS} from "@/medication-checkup/services/SceneClasses";
import CounsellingPlanService from "@/medication-checkup/services/CounsellingPlanService";
import {CounsellingPlanDraft, CounsellingPlanLine, CounsellingPlanView} from "@/medication-checkup/interfaces/counselling-plan";

/**
 * The card of the posology plan: the treatments that change, in the order
 * the HDS serves them — the stops leading — each with what changes, the
 * posology that stands and the advice for the patient; the patient full
 * screen at the foot. What does not change is only counted here: the full
 * screen shows the whole plan.
 *
 * While live, the posologies and the advice are the scene's drafts: the card
 * shows them and asks for them, the scene holds them until the validation.
 */

const props = defineProps<{
    view: CounsellingPlanView;
    frozen?: boolean;
    drafts?: Record<string, CounsellingPlanDraft>;
    busy?: boolean;
}>();

const emit = defineEmits<{
    edit: [line: CounsellingPlanLine];
    advice: [line: CounsellingPlanLine, text: string];
    fullscreen: [];
}>();

const GRID = 'grid grid-cols-[148px_minmax(0,1fr)_minmax(0,0.85fr)_minmax(0,0.85fr)] gap-3 px-4 border-t border-gray-light';

const SUBINPUT = 'flex-1 min-w-0 h-7 !border-solid !border-gray bg-white px-2 py-1 text-[11.5px] shadow-none';

const draftOf = (line: CounsellingPlanLine): CounsellingPlanDraft | undefined => props.drafts?.[CounsellingPlanService.key(line)];

const posologyOf = (line: CounsellingPlanLine): string | null => CounsellingPlanService.posology(line, draftOf(line));

const adviceOf = (line: CounsellingPlanLine): string => draftOf(line)?.advice ?? line.advice ?? '';

/**
 * The lines that change, the only ones the card lists.
 */
const changes = computed(() => props.view.lines.filter(line => line.status !== 'unchanged'));

const unchanged = computed(() => props.view.lines.length - changes.value.length);

/**
 * The posologies still to enter before the plan can be validated.
 */
const missing = computed(() => props.frozen
    ? 0
    : props.view.lines.filter(line => line.dosage_required && !draftOf(line)?.dosage).length
);
</script>

<template>
  <div :class="CARD_SHEET" class="rounded-xl overflow-hidden">
    <div class="flex items-center gap-2 px-4 py-3">
      <ClipboardList class="w-[18px] h-[18px] text-primary" />
      <span class="text-[13px] font-semibold">{{ view.title }}</span>
      <span class="text-[11.5px] text-muted-foreground">— {{ view.subtitle }}</span>
      <span class="flex-1" />
      <span v-if="missing > 0" class="text-xs font-semibold text-red-light">
        {{ missing }} posologie{{ missing > 1 ? 's' : '' }} à saisir
      </span>
    </div>

    <div :class="GRID" class="py-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
      <span>Statut</span>
      <span>Traitement</span>
      <span>Posologie du plan</span>
      <span>Conseil au patient</span>
    </div>

    <div
      v-for="(line, index) in changes"
      :key="CounsellingPlanService.key(line)"
      :class="GRID"
      class="py-2.5 items-start animate-message-in"
      :style="{animationDelay: `${index * 60}ms`}"
    >
      <span class="self-start px-2 py-1 rounded-full text-[10.5px] font-bold whitespace-nowrap" :class="STATUS_CHIPS[line.status]">
        {{ line.status_label }}
      </span>

      <span class="flex flex-col gap-0.5 min-w-0">
        <span class="text-xs font-semibold leading-tight" :class="{'line-through text-muted-foreground': line.status === 'stopped'}">
          {{ line.label }}
        </span>
        <span v-if="line.replacement" class="text-[11px] font-semibold text-primary">→ {{ line.replacement }}</span>
        <span v-if="line.before" class="text-[10.5px] text-muted-foreground">Avant : {{ line.before }}</span>
        <span v-if="line.after" class="text-[10.5px]">Retenu : {{ line.after }}</span>
        <span v-if="line.note" class="text-[10.5px] italic text-muted-foreground">{{ line.note }}</span>
      </span>

      <span class="flex flex-col items-start gap-1 min-w-0">
        <span v-if="line.status === 'stopped'" class="text-[11.5px] text-muted-foreground">À arrêter</span>
        <span v-else-if="posologyOf(line)" class="text-[11.5px]">{{ posologyOf(line) }}</span>
        <span v-else-if="!line.dosage_editable" class="text-[11.5px] italic text-muted-foreground">Posologie non renseignée</span>
        <button
          v-if="!frozen && line.dosage_editable"
          type="button"
          class="inline-flex items-center gap-1 rounded-md border border-solid px-2 py-1 text-[11px] font-semibold disabled:opacity-50"
          :class="line.dosage_required && !draftOf(line)?.dosage ? 'border-primary !text-primary' : 'border-gray !text-gray-dark'"
          :disabled="busy"
          @click="emit('edit', line)"
        >
          <Pencil class="w-3 h-3" />
          {{ draftOf(line)?.dosage ? 'Modifier' : 'Saisir la posologie' }}{{ line.dosage_required && !draftOf(line)?.dosage ? ' *' : '' }}
        </button>
      </span>

      <span class="flex items-center gap-1.5 min-w-0">
        <template v-if="!frozen">
          <MessageSquareText class="w-3.5 h-3.5 shrink-0 text-primary" />
          <Input
            :model-value="adviceOf(line)"
            :class="SUBINPUT"
            :disabled="busy"
            placeholder="Conseil (facultatif)…"
            @update:model-value="emit('advice', line, String($event))"
          />
        </template>
        <span v-else-if="adviceOf(line)" class="text-[11px]">{{ adviceOf(line) }}</span>
        <span v-else class="text-[11px] text-gray">—</span>
      </span>
    </div>

    <div v-if="changes.length === 0" class="px-4 py-3 border-t border-gray-light text-xs text-muted-foreground">
      Aucun changement : les traitements restent tels qu'au recueil.
    </div>

    <div v-if="changes.length > 0 && unchanged > 0" class="px-4 py-2 border-t border-gray-light text-[11px] text-muted-foreground">
      {{ unchanged }} traitement{{ unchanged > 1 ? 's' : '' }} inchangé{{ unchanged > 1 ? 's' : '' }}, présenté{{ unchanged > 1 ? 's' : '' }} en plein écran.
    </div>

    <div class="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 border-t border-gray-light">
      <button
        type="button"
        class="inline-flex items-center gap-2.5 rounded-full px-5 py-2.5 text-sm font-semibold !text-white bg-gradient-to-r from-primary-dark to-primary shadow-custom"
        @click="emit('fullscreen')"
      >
        <Maximize class="w-4 h-4" />
        Afficher en plein écran pour le patient
      </button>
      <span class="text-xs text-muted-foreground">grand format, simple à lire</span>
    </div>
  </div>
</template>
